import React, { useState } from 'react';
import { useTheme } from '../ThemeContext';
import SetupScreen from './SetupScreen';
import { setHomeownerPassword } from '../utils/homeownerApi';
import {
  HOMEOWNER_BUTTON,
  HOMEOWNER_CONSENT_ERROR,
  homeownerInviteCopy,
} from '../shared/consentCopy';

const linkStyle = { color: '#0f766e', fontWeight: 650 };

/**
 * First-time homeowners agree to the terms and set a password before the dashboard.
 * There is no skip.
 */
const HomeownerPasswordGate = ({ client, agent, agentLoading = false, onDone, onLogout }) => {
  const { colors } = useTheme();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const copy = homeownerInviteCopy(agent);
  const waitingForAgent = agentLoading === true;
  const canSubmit = !waitingForAgent && copy.nameKnown && accepted && !saving;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!accepted) {
      setError(HOMEOWNER_CONSENT_ERROR);
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const result = await setHomeownerPassword(password, undefined, { accepted: true });
      if (onDone) await onDone(result && result.client);
    } catch (saveError) {
      setError(saveError.message || 'Could not set that password. Please try again.');
      setSaving(false);
    }
  };

  return (
    <SetupScreen centered>
      <form
        onSubmit={handleSubmit}
        style={{
          width: '100%',
          maxWidth: '32rem',
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: '1rem',
          padding: '1.5rem',
          color: colors.text,
          fontSize: '1rem',
          lineHeight: 1.45,
        }}
      >
        {waitingForAgent ? (
          <div
            role="status"
            aria-live="polite"
            aria-busy="true"
            aria-label="Loading"
            style={{ margin: '0 0 1rem' }}
          >
            <div style={{ height: '1rem', width: '80%', borderRadius: '0.4rem', background: colors.border, marginBottom: '0.5rem' }} />
            <div style={{ height: '1rem', width: '55%', borderRadius: '0.4rem', background: colors.border }} />
          </div>
        ) : (
          <>
            <p style={{ marginTop: 0 }}>{copy.intro}</p>
            <p>
              <strong>{copy.sharing}</strong>
              {' '}
              {copy.control}
            </p>
          </>
        )}
        <input
          type="password"
          aria-label="New password"
          placeholder="New password (min 8 characters)"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          minLength={8}
          required
          autoComplete="new-password"
          style={inputStyle(colors)}
        />
        <input
          type="password"
          aria-label="New password again"
          placeholder="New password again"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          minLength={8}
          required
          autoComplete="new-password"
          style={inputStyle(colors)}
        />
        <label style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', margin: '0.5rem 0 1rem' }}>
          <input
            type="checkbox"
            checked={accepted}
            onChange={(event) => {
              setAccepted(event.target.checked);
              if (event.target.checked) setError('');
            }}
            style={{ marginTop: '0.2rem', width: '1.1rem', height: '1.1rem', flexShrink: 0 }}
          />
          <span>
            I am 18 or older and agree to the{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" style={linkStyle}>Terms of Service</a>
            {' '}
            (including the <strong>arbitration agreement and class action waiver</strong>, which I may opt out of within 30 days), and I have read the{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" style={linkStyle}>Privacy Policy</a>.
          </span>
        </label>
        {error && <p role="alert" style={{ color: '#b91c1c', marginTop: 0 }}>{error}</p>}
        <button
          type="submit"
          disabled={!canSubmit}
          style={{
            width: '100%',
            padding: '0.85rem',
            border: 'none',
            borderRadius: '0.5rem',
            background: '#59c4c4',
            color: 'white',
            fontWeight: 700,
            fontSize: '1rem',
            cursor: canSubmit ? 'pointer' : 'not-allowed',
            opacity: canSubmit ? 1 : 0.55,
          }}
        >
          {saving ? 'Saving...' : HOMEOWNER_BUTTON}
        </button>
        {onLogout && (
          <button
            type="button"
            onClick={onLogout}
            style={{
              width: '100%',
              marginTop: '0.75rem',
              padding: '0.85rem',
              borderRadius: '0.5rem',
              border: `1px solid ${colors.border}`,
              background: 'transparent',
              color: colors.text,
              fontSize: '1rem',
              cursor: 'pointer',
            }}
          >
            Log out
          </button>
        )}
      </form>
    </SetupScreen>
  );
};

function inputStyle(colors) {
  return {
    width: '100%',
    boxSizing: 'border-box',
    marginBottom: '0.75rem',
    padding: '0.75rem',
    borderRadius: '0.5rem',
    border: `2px solid ${colors.border}`,
    background: colors.surface,
    color: colors.text,
    fontSize: '1rem',
  };
}

export default HomeownerPasswordGate;
